import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupPresentationProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Info from "@insite/mobius/Icons/Info";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { shippingAddressStyles } from "@tKgpco/Overrides/Widgets/CheckoutShipping/ShippingAddress";
import setInstruction from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/SpecialDeliveryInstructionHandler";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    return {
        cart: cartState,
    };
};

const mapDispatchToProps = {
    setInstruction,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SpecialDeliveryInstruction {
    container?: GridContainerProps;
    instructionGridItem?: GridItemProps;
    textHeader?: TypographyPresentationProps;
    textNote?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    CheckboxGroupWapper?: InjectableCss;
    checkbox?: CheckboxProps;
    CheckboxGroup?: CheckboxGroupPresentationProps;
    shipTooltip?: TooltipPresentationProps;
}

export const specialDeliveryInstruction: SpecialDeliveryInstruction = {
    wrapper: {
        css: css`
            width: 100%;
            > div {
                vertical-align: bottom;
            }
        `,
    },
    textHeader: {
        size: TYPOGRAPHY.size.smallest,
        weight: TYPOGRAPHY.weight.bold,
    },
    instructionGridItem: {
        width: [12, 6, 4, 4, 4],
        css: css`
            padding: 10px 15px;
            padding-bottom: 0;
        `,
    },
    CheckboxGroup: {
        css: css`
            flex-direction: row;
            margin: 10px 0;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    shipTooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 5px;
            `,
        },
        iconProps: {
            src: Info,
            css: css`
                svg {
                    color: ${COLORS.primary.base};
                    width: 18px;
                    height: 18px;
                }
            `,
        },
    },
};

const styles = specialDeliveryInstruction;
const SpecialDeliveryInstruction = ({ cart, setInstruction }: Props) => {
    const instructions = cart?.properties.specialDeliveryInstruction?.split("|");
    const userSelectedInstructions = cart?.properties?.userSelectedSpecialDeliveryInstruction?.split("|");
    const userInstructionsModifyArr: { instruction: string; isChecked: boolean | undefined }[] = [];
    const [instructionsString, setInstructionsString] = React.useState("");
    const [instructionsArray, setInstructionsArray] = React.useState<string[]>([]);
    
    let duplicateArray: string[];
    React.useEffect(() => {
        if (cart?.isAwaitingApproval && userSelectedInstructions) {
            duplicateArray = userSelectedInstructions;
            setInstructionsArray(duplicateArray);
        }
    }, []);

    React.useEffect(() => {
        setInstruction({ instructionsArray });
    }, [instructionsArray, setInstructionsArray]);

    if (instructions === undefined && userSelectedInstructions === undefined) {
        return null;
    }
    instructions &&
        // eslint-disable-next-line array-callback-return
        instructions.map((v, i) => {
            const selected = userSelectedInstructions?.includes(v);
            if (selected) {
                userInstructionsModifyArr.push({
                    instruction: v,
                    isChecked: selected,
                });
            } else {
                userInstructionsModifyArr.push({
                    instruction: v,
                    isChecked: selected || false,
                });
            }
        });
    const removeDuplicateValues = (arr: string[], value: string) => {
        return arr.filter(ele => ele !== value);
    };

    const pushInstructionIntoArrayHandler = (instructionValue: string) => {
        if (instructionsArray.find(instruction => instruction === instructionValue) === undefined) {
            duplicateArray = [...instructionsArray, instructionValue];

            setInstructionsArray(duplicateArray);
        } else {
            setInstructionsArray(removeDuplicateValues(instructionsArray, instructionValue));
        }
    };

    const onChangeHandler = (e: any, key: any) => {	
        const selectedArray: string[] = [...instructionsArray];	
        if (e.target.getAttribute("aria-checked") === "false") {	
            selectedArray.push(key);	
        } else {	
            selectedArray.splice(selectedArray.indexOf(key), 1);	
        }	
        setInstructionsArray(selectedArray);	
    };	

    // const onChangeHandler = (e: any) => {	
    //     pushInstructionIntoArrayHandler(e.currentTarget.textContent);	
    // };	
    

    return (
            <StyledWrapper {...styles.wrapper}>
                <Typography as="span" {...styles.textHeader}>
                    Special Delivery Instructions
                </Typography>
                <Tooltip
                    {...styles.shipTooltip}
                    className="toolTip-wrapper"
                    text={siteMessage("Please note: Special delivery may have charges that are non-refundable") as string}
                />
                <GridContainer {...styles.container}>
                    {userInstructionsModifyArr?.map((value, index) => (
                        <GridItem {...styles.instructionGridItem} key={value.instruction}>
                            <CheckboxGroup
                                {...styles.CheckboxGroup}
                                onClick={e => onChangeHandler(e, value.instruction)}
                                name={value.instruction}
                                defaultValue={value.instruction}
                            >
                                <Checkbox
                                    {...styles.checkbox}
                                    checked={value.isChecked}
                                    key={value.instruction}
                                    data-test-selector="orderReview-instructions"
                                >
                                    {value.instruction}
                                </Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SpecialDeliveryInstruction),
    definition: {
        displayName: "Special Delivery Instructions Checkbox",
        group: "Checkout - Review & Submit",
    },
};

export default widgetModule;
